import React from 'react';
import { formatStoreCurrency } from '../../utils/storeUtils';

function DeltaLine({ value, mode, invert = false, isDarkMode, priorLabel }) {
  const muted = isDarkMode ? 'text-slate-500' : 'text-slate-400';

  if (priorLabel) {
    return (
      <div className={`text-[10px] tabular-nums truncate ${muted}`} title={priorLabel}>
        Prior {priorLabel}
      </div>
    );
  }

  if (value == null || !Number.isFinite(value)) {
    return <div className={`text-[10px] ${muted}`}>—</div>;
  }

  if (value === 0) {
    return <div className={`text-[10px] tabular-nums ${muted}`}>0% vs prior</div>;
  }

  const positive = invert ? value < 0 : value > 0;
  const negative = invert ? value > 0 : value < 0;
  const color = positive
    ? (isDarkMode ? 'text-emerald-400' : 'text-emerald-600')
    : negative
      ? (isDarkMode ? 'text-rose-400' : 'text-rose-600')
      : muted;

  let text;
  if (mode === 'points') {
    const sign = value > 0 ? '+' : '';
    text = `${sign}${value.toFixed(1)}pp`;
  } else if (mode === 'roas') {
    const sign = value > 0 ? '+' : '';
    text = `${sign}${value.toFixed(2)}×`;
  } else {
    const sign = value > 0 ? '+' : '';
    text = `${sign}${value.toFixed(0)}%`;
  }

  return (
    <div className={`text-[10px] font-medium tabular-nums ${color}`}>
      {text} vs prior
    </div>
  );
}

function MetricCell({
  value,
  delta,
  deltaMode = 'percent',
  invertDelta = false,
  priorFormatted,
  noPriorNote = false,
  loading,
  isDarkMode,
}) {
  const muted = isDarkMode ? 'text-slate-500' : 'text-slate-400';
  return (
    <div className="text-right min-w-0">
      {loading ? (
        <div className={`h-4 w-14 ml-auto rounded animate-pulse ${isDarkMode ? 'bg-slate-700' : 'bg-slate-200'}`} />
      ) : (
        <>
          <div className={`text-sm font-semibold tabular-nums ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>
            {value ?? '—'}
          </div>
          <DeltaLine
            value={delta}
            mode={deltaMode}
            invert={invertDelta}
            isDarkMode={isDarkMode}
          />
          {priorFormatted ? (
            <DeltaLine isDarkMode={isDarkMode} priorLabel={priorFormatted} />
          ) : noPriorNote ? (
            <div className={`text-[10px] ${muted}`}>No prior 30d</div>
          ) : null}
        </>
      )}
    </div>
  );
}

/**
 * Four table cells (spend, sales, acos, roas) — parent must be CSS grid row.
 */
export default function StorePerformanceMetrics({
  summary,
  currency,
  loading = false,
  isDarkMode,
  layout = 'row',
}) {
  const cur = summary?.current;
  const prev = summary?.previous;
  const d = summary?.deltaPct;
  const dp = summary?.deltaPoints;

  const spendStr = cur ? formatStoreCurrency(cur.spend, currency) : null;
  const salesStr = cur ? formatStoreCurrency(cur.sales, currency) : null;
  const acosStr = cur?.acos != null && Number.isFinite(cur.acos) ? `${cur.acos.toFixed(1)}%` : null;
  const roasStr = cur?.roas != null && Number.isFinite(cur.roas) ? `${cur.roas.toFixed(2)}×` : null;

  const priorSpend = prev ? formatStoreCurrency(prev.spend, currency) : null;
  const priorSales = prev ? formatStoreCurrency(prev.sales, currency) : null;
  const priorAcos = prev?.acos != null && Number.isFinite(prev.acos) ? `${prev.acos.toFixed(1)}%` : null;
  const priorRoas = prev?.roas != null && Number.isFinite(prev.roas) ? `${prev.roas.toFixed(2)}×` : null;

  const hasPrior = prev && (prev.spend > 0 || prev.sales > 0);
  const hasCurrent = cur && (cur.spend > 0 || cur.sales > 0);
  const missingPrior = hasCurrent && !hasPrior;

  const cells = (
    <>
      <MetricCell
        value={spendStr}
        delta={d?.spend}
        priorFormatted={hasPrior ? priorSpend : null}
        noPriorNote={missingPrior}
        loading={loading}
        isDarkMode={isDarkMode}
      />
      <MetricCell
        value={salesStr}
        delta={d?.sales}
        priorFormatted={hasPrior ? priorSales : null}
        noPriorNote={missingPrior}
        loading={loading}
        isDarkMode={isDarkMode}
      />
      <MetricCell
        value={acosStr}
        delta={dp?.acos}
        deltaMode="points"
        invertDelta
        priorFormatted={hasPrior ? priorAcos : null}
        loading={loading}
        isDarkMode={isDarkMode}
      />
      <MetricCell
        value={roasStr}
        delta={dp?.roas}
        deltaMode="roas"
        priorFormatted={hasPrior ? priorRoas : null}
        loading={loading}
        isDarkMode={isDarkMode}
      />
    </>
  );

  if (!summary) {
    const empty = loading ? (
      <>
        {[0, 1, 2, 3].map((i) => (
          <MetricCell key={i} loading value={null} isDarkMode={isDarkMode} />
        ))}
      </>
    ) : (
      <>
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className={`text-right text-xs py-1 ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
            {i === 0 ? 'No 30d data' : ''}
          </div>
        ))}
      </>
    );
    return empty;
  }

  return cells;
}
